import { useContext } from "react";
import { styled } from "@linaria/react";
import { EXCLUSIVE_FLAG, getGameContext } from "../../data/derived";
import { isGameId } from "../../games-list";
import { registerTooltip } from "../Tooltip";
import { GameIcon, KindIcon } from "../kind-icon/KindIcon";
import { DeclarationsContext } from "../schema/DeclarationsContext";
import { flagAccent, flagDescription, flagIcon } from "./flags";

/** The exclusive flag's icon, the game's own */
export function ExclusiveIcon() {
  const { game } = useContext(DeclarationsContext);
  return <GameIcon game={game} />;
}

/**
 * Flag label, with an icon for the flags that need to stand out. The exclusive flag is only
 * its icon in the lists, where every other row would repeat the same word
 */
export function FlagContent({ flag, compact }: { flag: string; compact?: boolean }) {
  if (flag === EXCLUSIVE_FLAG) {
    return (
      <>
        <ExclusiveIcon />
        {compact ? <VisuallyHidden>{flag}</VisuallyHidden> : flag}
      </>
    );
  }
  const icon = flagIcon(flag);
  return (
    <>
      {icon && <KindIcon kind={icon} />}
      {flag}
    </>
  );
}

/** Read by screen readers, not shown */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

/** Tooltip props for a flag's badge or filter, `hint` says what clicking it does */
export function flagTip(flag: string, game: string, hint?: string) {
  return {
    "data-tip-kind": "flag",
    "data-flag": flag,
    "data-tip-game": game,
    "data-tip-hint": hint,
    "data-tip-accent": flagAccent(flag),
  };
}

registerTooltip("flag", (el) => {
  const { flag = "", tipHint, tipGame } = el.dataset;
  const description = flagDescription(flag);
  if (!description && !tipHint) return null;
  const content = <FlagTooltipContent flag={flag} description={description} hint={tipHint} />;
  // The host sits outside the page. The exclusive flag's icon is the badge's game
  return tipGame && isGameId(tipGame) ? (
    <DeclarationsContext.Provider value={getGameContext(tipGame)}>
      {content}
    </DeclarationsContext.Provider>
  ) : (
    content
  );
});

/** Tooltip body: the flag's own badge as a heading, colored to match, then its description.
 *  Shared by the row badges and the sidebar's flag filters so both look identical. */
export function FlagTooltipContent({
  flag,
  description,
  hint,
}: {
  flag: string;
  description?: string;
  hint?: string;
}) {
  return (
    <>
      <TooltipFlagName>
        <FlagContent flag={flag} />
      </TooltipFlagName>
      {description && <TooltipDescription>{description}</TooltipDescription>}
      {hint && <TooltipHint>{hint}</TooltipHint>}
    </>
  );
}

const TooltipFlagName = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
  font-weight: 600;
  color: var(--accent, var(--text));

  > svg {
    flex-shrink: 0;
    border-radius: 2px;
  }
`;

const TooltipDescription = styled.div`
  color: var(--text);
`;

const TooltipHint = styled.div`
  margin-top: 6px;
  color: var(--text-dim);
  font-style: italic;
`;
